import TiktokIcon from '@/components/icons/tiktok-icon';
import { ElementProps } from '@/components/sections/builder/types';
import Image from 'next/image';

import {
  FooterElementDataType,
  FooterSectionSocialMediaType,
} from '@/components/sections/builder/elements/footer/footer/types';

import { useBuilderStore } from '@/stores/builder';
import {
  BetweenHorizontalStart,
  FacebookIcon,
  InstagramIcon,
  TwitterIcon,
  YoutubeIcon,
} from 'lucide-react';
import Link from 'next/link';
import React from 'react';
import { shallow } from 'zustand/shallow';
import InputTab from '@/components/sections/builder/settings/input-tab';
import TextEditorMinimal from '@/components/commons/minimal-tiptap/minimal-editor';
import clsx from 'clsx';

const defaultData: FooterSectionSocialMediaType = {
  name: 'Social Media',
  heading: 'Social Media',
  alignment: 'left',
};

export const ElementContent = ({ element }: ElementProps) => {
  const data = (element.data || defaultData) as FooterSectionSocialMediaType;
  const parentData = (element.parentData || {}) as FooterElementDataType;

  const SOCIAL_MEDIA = [
    { key: 'twitter', label: 'Twitter', icon: <TwitterIcon /> },
    { key: 'facebook', label: 'Facebook', icon: <FacebookIcon /> },
    { key: 'instagram', label: 'Instagram', icon: <InstagramIcon /> },
    { key: 'youtube', label: 'Youtube', icon: <YoutubeIcon /> },
    {
      key: 'tiktok',
      label: 'Tiktok',
      icon: <TiktokIcon color={parentData.colorSchemeFooter?.text || '#52535B'} />,
    },
  ];

  return (
    <>
      <div className='h-full w-full'>
        <h2
          className='content-footer-text mb-1 text-lg font-medium'
          dangerouslySetInnerHTML={{ __html: data.heading || '' }}
        ></h2>
        <div
          className={clsx(
            'flex h-full w-full flex-col',
            data.alignment === 'left' && 'items-start',
            data.alignment === 'center' && 'items-center',
            data.alignment === 'right' && 'items-end'
          )}
        >
          {SOCIAL_MEDIA.map((social) => {
            const keyUrl = `${social.key}Url` as keyof FooterElementDataType;
            const keyText = `${social.key}Text` as keyof FooterElementDataType;
            const keyIconUrl = `${social.key}IconUrl` as keyof FooterElementDataType;

            if (!parentData[keyUrl]) {
              return null;
            }

            return (
              <Link
                key={social.key}
                href={parentData[keyUrl] as string}
                className='mb-2 flex items-center space-x-3'
              >
                <div className='flex h-6 w-6'>
                  {parentData[keyIconUrl] ? (
                    <Image
                      src={parentData[keyIconUrl] as string}
                      width={24}
                      height={24}
                      layout='responsive'
                      alt={`icon-${social.key}`}
                      className='object-contain'
                    />
                  ) : (
                    social.icon
                  )}
                </div>
                <div style={{ width: 'calc(100% - 24px)' }}>
                  {(parentData[keyText] || social.label) as string}
                </div>
              </Link>
            );
          })}
        </div>
      </div>
      <style jsx>
        {`
          .content-footer-text {
            text-align: ${data.alignment || parentData.alignment || 'left'};
          }
        `}
      </style>
    </>
  );
};

export const ElementPreviewIcon = BetweenHorizontalStart;

export function ElementSetting({ element }: ElementProps) {
  const builder = useBuilderStore(
    (state) => ({
      setChildData: state.setChildData,
      clearSelected: state.clearSelected,
    }),
    shallow
  );

  const data = element.data as FooterSectionSocialMediaType;

  return (
    <div>
      <div className='mb-3'>
        <h3 className='mb-2 text-xs font-bold'>Heading</h3>
        <TextEditorMinimal
          value={data.heading as string}
          onChange={(val) => {
            builder.setChildData(element.parentId as string, element.id, {
              heading: val,
            });
          }}
          output='html'
          editorProps={{
            attributes: {
              class: 'prose lg:prose-lg prose-blue dark:prose-invert p-2',
            },
          }}
          editable
        />
      </div>
      <div className='mb-1 text-xs font-bold'>Alignment</div>
      <div className='mb-4'>
        <InputTab
          defaultValue={data.alignment}
          options={[
            { label: 'Left', value: 'left' },
            { label: 'Center', value: 'center' },
            { label: 'Right', value: 'right' },
          ]}
          onChange={(v) => {
            builder.setChildData(element.parentId as string, element.id, {
              alignment: v,
            });
          }}
        />
      </div>
    </div>
  );
}

const FooterSectionSocialMedia = {
  type: 'footer_section_social_media',
  group: 'footer_section',
  defaultData,
  Element: ElementContent,
  Setting: ElementSetting,
  PreviewIcon: ElementPreviewIcon,
  Icon: ElementPreviewIcon,
  childs: [],
};

export default FooterSectionSocialMedia;
